/** Category and specialism navigation, from the fixed shared taxonomy. */

import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { ReactNode, RefObject } from 'react';

import { TAXONOMY, getCategory } from '@bidtohire/shared';

/**
 * Marks a horizontal rail with the end or ends it can still be scrolled
 * towards, which the stylesheet turns into a fade. The class is only ever set
 * when there is genuinely something behind that edge, so a rail whose chips
 * all fit keeps its hard edges and a rail scrolled to its end stops fading the
 * chip the reader has just arrived at.
 *
 * `deps` re-runs the wiring when the chips themselves change, since a new
 * category is a different set of widths and the old observers watch elements
 * that are no longer on the page.
 */
function useEdgeFades(deps: readonly unknown[]): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const rail = ref.current;
    if (rail === null) return undefined;

    const update = (): void => {
      const travel = rail.scrollWidth - rail.clientWidth;
      // Sub-pixel remainders are rounding, not content: a rail is only
      // scrollable if there is more than a pixel of it to go.
      const scrollable = travel > 1;
      const at = rail.scrollLeft;
      rail.classList.toggle('has-lead-fade', scrollable && at > 1);
      rail.classList.toggle('has-trail-fade', scrollable && at < travel - 1);
    };

    update();
    rail.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    // The chips are sized by a webfont, so their widths land after the first
    // paint. Without this the rail measures itself against fallback metrics.
    let live = true;
    if (typeof document.fonts?.ready?.then === 'function') {
      void document.fonts.ready.then(() => {
        if (live) update();
      });
    }

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(update);
      observer.observe(rail);
      for (const chip of rail.children) observer.observe(chip);
    }

    return () => {
      live = false;
      rail.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      observer?.disconnect();
    };
  }, deps);

  return ref;
}

export function CategoryGrid({ limit }: { readonly limit?: number }): ReactNode {
  const shown = limit === undefined ? TAXONOMY : TAXONOMY.slice(0, limit);
  const remaining = TAXONOMY.length - shown.length;
  const subcategoryCount = TAXONOMY.reduce(
    (sum, category) => sum + category.subcategories.length,
    0,
  );

  return (
    <nav className="catgrid" aria-label="Categories">
      {shown.map((category) => (
        <Link key={category.slug} className="card catcard" to={`/c/${category.slug}`}>
          <div className="name">{category.name}</div>
          <div className="foot">
            <span>
              {category.subcategories.length}{' '}
              {category.subcategories.length === 1 ? 'specialism' : 'specialisms'}
            </span>
            <span className="price mono">View board →</span>
          </div>
        </Link>
      ))}

      {remaining > 0 ? (
        <Link className="card catcard is-all" to="/categories">
          <div className="name">All {TAXONOMY.length} categories</div>
          <div className="foot">
            <span>{subcategoryCount} specialisms</span>
          </div>
        </Link>
      ) : null}
    </nav>
  );
}

export function SubcategoryChips({
  category,
  active,
}: {
  readonly category: string;
  readonly active?: string;
}): ReactNode {
  const found = getCategory(category);
  const rail = useEdgeFades([category]);
  if (found === undefined) return null;

  return (
    <div className="chiprow is-scroll" ref={rail} aria-label={`${found.name} specialisms`}>
      <Link
        className={active === undefined ? 'chip is-on' : 'chip'}
        to={`/c/${found.slug}`}
      >
        All {found.name}
      </Link>
      {found.subcategories.map((subcategory) => (
        <Link
          key={subcategory.slug}
          className={active === subcategory.slug ? 'chip is-on' : 'chip'}
          to={`/c/${found.slug}/${subcategory.slug}`}
        >
          {subcategory.name}
        </Link>
      ))}
    </div>
  );
}
